import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { getHubspotUtk } from "@/lib/hubspot";
import { trackEvent, AnalyticsEvents } from "@/utils/analytics";

interface Message {
  role: "user" | "assistant";
  content: string;
  buttons?: Array<{ label: string; value: string }>;
}

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hallo! Ik ben de PlayfieldVR assistent. 🎮\n\nWij zijn een mobiele VR service - we komen naar jullie locatie toe! Thuis, op kantoor of een andere locatie - wij brengen de complete VR ervaring naar jullie.\n\nHoe kan ik je helpen?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const streamChat = async (userMessage: string) => {
    setIsLoading(true);
    const newMessages = [...messages, { role: "user" as const, content: userMessage }];
    setMessages(newMessages);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-bot`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          }),
        }
      );

      if (!response.ok || !response.body) {
        throw new Error("Failed to start stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;
      let assistantMessage = "";
      let functionCallBuffer = "";
      let currentFunctionCall: { name: string; arguments: string } | null = null;

      // Add empty assistant message
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            
            // Handle function call
            const toolCalls = parsed.choices?.[0]?.delta?.tool_calls;
            if (toolCalls && toolCalls[0]) {
              const toolCall = toolCalls[0];
              if (toolCall.function?.name) {
                currentFunctionCall = { name: toolCall.function.name, arguments: "" };
              }
              if (toolCall.function?.arguments) {
                functionCallBuffer += toolCall.function.arguments;
              }
            }

            // Handle regular content
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantMessage += content;
              
              // Parse buttons from content
              let displayContent = assistantMessage;
              let buttons: Array<{ label: string; value: string }> | undefined;
              
              const buttonMatch = assistantMessage.match(/\[BUTTONS\]([\s\S]*?)\[\/BUTTONS\]/);
              if (buttonMatch) {
                displayContent = assistantMessage.replace(/\[BUTTONS\][\s\S]*?\[\/BUTTONS\]/, '').trim();
                const buttonLines = buttonMatch[1].trim().split('\n');
                buttons = buttonLines.map(line => {
                  const [label, value] = line.split('|');
                  return { label: label.trim(), value: value.trim() };
                }).filter(b => b.label && b.value);
              }
              
              setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = {
                  ...newMessages[newMessages.length - 1],
                  content: displayContent,
                  buttons
                };
                return newMessages;
              });
            }

            // Check if function call is complete
            if (parsed.choices?.[0]?.finish_reason === "tool_calls" && currentFunctionCall && functionCallBuffer) {
              try {
                const args = JSON.parse(functionCallBuffer);
                if (currentFunctionCall.name === "collect_contact_info") {
                  // Submit the contact form
                  const { supabase } = await import("@/integrations/supabase/client");
                  const { error } = await supabase.functions.invoke("submit-contact-form", {
                    body: {
                      name: args.name,
                      email: args.email,
                      phone: args.phone,
                      company: args.company || "",
                      message: args.message,
                      hutk: getHubspotUtk(),
                      pageUri: typeof window !== "undefined" ? window.location.href : undefined,
                      pageName: typeof document !== "undefined" ? document.title : undefined,
                    },
                  });

                  if (error) {
                    console.error("Error submitting contact form:", error);
                    trackEvent(AnalyticsEvents.FORM_ERROR, { source: 'chatbot' });
                    setMessages(prev => [
                      ...prev,
                      {
                        role: "assistant",
                        content: "Bedankt voor je gegevens! Ik heb een klein technisch probleem ondervonden, maar geen zorgen - neem contact op via info@playfield.nl en ons team helpt je graag verder!",
                      },
                    ]);
                  } else {
                    trackEvent(AnalyticsEvents.CONTACT_SUBMIT, { source: 'chatbot' });
                    setMessages(prev => [
                      ...prev,
                      {
                        role: "assistant",
                        content: "Geweldig! 🎉 Ik heb je gegevens doorgestuurd naar ons team. We nemen zo snel mogelijk contact met je op via email of telefoon. Heb je nog andere vragen in de tussentijd?",
                      },
                    ]);
                  }
                }
              } catch (e) {
                console.error("Error parsing function call:", e);
              }
              currentFunctionCall = null;
              functionCallBuffer = "";
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, er ging iets mis. Neem contact op via info@playfield.nl",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    trackEvent(AnalyticsEvents.CHAT_MESSAGE_SENT, { message_length: input.trim().length });
    streamChat(input);
    setInput("");
  };

  const handleButtonClick = (value: string) => {
    streamChat(value);
  };

  const scrollToContact = () => {
    const element = document.getElementById("contact-form");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Floating Buttons */}
      {!isOpen && (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 flex flex-col gap-3 z-50">
          <Button
            onClick={() => {
              if (!hasTrackedOpen.current) {
                trackEvent(AnalyticsEvents.CHAT_OPENED, { source: 'floating_button' });
                hasTrackedOpen.current = true;
              }
              setIsOpen(true);
            }}
            className="h-14 w-14 rounded-full bg-primary hover:bg-primary/90 self-end shadow-[0_4px_14px_rgba(0,0,0,0.5)] border-2 border-background/80 hover:scale-110 transition-all duration-300"
            size="icon"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
          <Button
            onClick={() => {
              trackEvent(AnalyticsEvents.BOOK_NOW, { location: 'floating_button' });
              scrollToContact();
            }}
            className="h-12 px-5 rounded-full bg-primary hover:bg-primary/90 font-semibold shadow-[0_4px_14px_rgba(0,0,0,0.5)] border-2 border-background/80 hover:scale-105 transition-all duration-300"
          >
            Boek nu
          </Button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed bg-card border border-border rounded-lg shadow-2xl z-50 flex flex-col ${
          isMobile 
            ? 'inset-4 top-20' 
            : 'bottom-6 right-6 w-[380px] h-[500px]'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <h3 className="font-semibold">PlayfieldVR Chat</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className="max-w-[80%] flex flex-col gap-2">
                    <div
                      className={`rounded-lg p-3 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.buttons && message.buttons.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {message.buttons.map((button, btnIndex) => (
                          <Button
                            key={btnIndex}
                            onClick={() => handleButtonClick(button.value)}
                            variant="outline"
                            className="w-full justify-start text-left"
                            disabled={isLoading}
                          >
                            {button.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg p-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce delay-200" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Typ je vraag..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;
